import type { CanvasFont } from "./canvas/engine/export";

// The canvas theme's font families for Planroom's own engine: loaded on screen
// and embedded in exports. Files come from the fonts' OFL releases (Fontsource
// builds), one per script subset. Medium weights serve as regular, as boards
// have always shown them.

const css = import.meta.glob(
  [
    "/node_modules/@fontsource/ibm-plex-{sans,serif,mono}/{500,700}{,-italic}.css",
    "/node_modules/@fontsource/shantell-sans/{400,700}{,-italic}.css",
  ],
  { query: "?raw", import: "default", eager: true },
) as Record<string, string>;
const files = import.meta.glob(
  [
    "/node_modules/@fontsource/ibm-plex-{sans,serif,mono}/files/*-{500,700}-{normal,italic}.woff2",
    "/node_modules/@fontsource/shantell-sans/files/*-{400,700}-{normal,italic}.woff2",
  ],
  { query: "?url", import: "default", eager: true },
) as Record<string, string>;

const FAMILY: Record<string, string> = {
  "ibm-plex-sans": "IBM Plex Sans",
  "ibm-plex-serif": "IBM Plex Serif",
  "ibm-plex-mono": "IBM Plex Mono",
  "shantell-sans": "Shantell Sans",
};

/** Read each @font-face of a Fontsource stylesheet into a canvas font. */
function fontsFrom(path: string, text: string): CanvasFont[] {
  const pkg = /@fontsource\/([^/]+)\//.exec(path)![1];
  return [...text.matchAll(/@font-face\s*{([^}]*)}/g)].flatMap(([, body]) => {
    const file = /url\(\.\/files\/([^)]+\.woff2)\)/.exec(body)?.[1];
    const src = file && files[`/node_modules/@fontsource/${pkg}/files/${file}`];
    if (!src) return [];
    const weight = Number(/font-weight:\s*(\d+)/.exec(body)?.[1] ?? 400);
    return [
      {
        family: FAMILY[pkg],
        src,
        // Medium (500) and the draw font's 400 are the regular face.
        weight: weight >= 700 ? "700 900" : "400 600",
        style: /font-style:\s*italic/.test(body) ? "italic" : "normal",
        unicodeRange: /unicode-range:\s*([^;]+)/.exec(body)?.[1].trim(),
      },
    ];
  });
}

export const canvasFonts: CanvasFont[] = Object.entries(css).flatMap(([path, text]) => fontsFrom(path, text));
